import type { FilterableColumn } from 'app/filter.utils'

/**
 * The URL params that make up "clear every filter on this table".
 *
 * Three callers need this answer and drifted apart once already:
 * `useTableFilters.clearFilters`, and the `clearAll` in each of the two table
 * shells. The shells cannot reuse `clearFilters` itself because it reads that
 * hook's internal filter state, so this covers the part all three derive the
 * same way: from the declared columns plus the current URL.
 *
 * Values follow `useHandleSearchParams`: `undefined` deletes a param, `''`
 * writes it back as `param=`. `useTableFilters` reads "present but empty" as
 * cleared and "absent" as "fall back to the declared defaults", so the empty
 * string is deliberate for everything except `join`.
 */
export function buildClearedFilterParams({
  columns,
  searchParams,
  customSearchParam = `filters`,
}: {
  columns?: FilterableColumn[]
  searchParams: URLSearchParams
  customSearchParam?: string
}): Record<string, string | undefined> {
  const customParams = (columns ?? [])
    .flatMap((column) =>
      Array.isArray(column.customSearchParam) ? column.customSearchParam : [column.customSearchParam],
    )
    .filter((param): param is string => !!param)
    .reduce<Record<string, string>>((acc, param) => ({ ...acc, [param]: ``, [`${param}Op`]: `` }), {})

  const secondaryAttributeParams = (columns ?? [])
    .map((column) => column.secondaryAttribute)
    .filter((param): param is string => !!param)
    .reduce<Record<string, string>>((acc, param) => ({ ...acc, [param]: `` }), {})

  return {
    [customSearchParam]: ``,
    // `join` is the leftover from an OR combination. It is deleted rather than
    // emptied, matching clearFilters, and only when it is actually set.
    ...(searchParams.has(`join`) && { join: undefined }),
    ...secondaryAttributeParams,
    ...customParams,
  }
}
